/**
 * Gravação em localStorage à prova de quota.
 *
 * Porquê: as camadas de segurança do Sobe & Desce gravam o estado do jogo em
 * várias chaves. Medido: um jogo com o histórico de undo cheio chega a 1,8 MB
 * por cópia — vezes 4 camadas passa dos 5 MB de quota típica do browser. Ao
 * estourar, TODOS os `setItem` passavam a falhar e o código só fazia
 * `console.warn`: o jogo continuava a ser jogado mas o que estava guardado
 * congelava (os "jogos presos no PC").
 *
 * Estratégia em escada — só sobe um degrau se o anterior falhar:
 *   1. gravar normalmente
 *   2. quota cheia → limpar restos de jogos antigos e tentar de novo
 *   3. ainda cheia → largar o histórico de undo/redo (é conveniência, não é o
 *      jogo) e tentar de novo
 *   4. ainda cheia → devolver false para quem chama decidir (nunca em silêncio)
 */

import { cleanupStaleGameState } from './cleanupStaleGameState';
import { reportarFalha } from '@/services/reporterDeFalhas';

/**
 * Aviso discreto ao utilizador. Importado de forma tardia para não arrastar o
 * sistema de toasts (React) para dentro de um utilitário de armazenamento, e
 * para nunca rebentar se for chamado fora do React.
 */
function avisarUtilizador(mensagem: string): void {
  import('@/services/toastService')
    .then(({ toastService }) => toastService.error(mensagem))
    .catch(() => {
      /* sem UI disponível — o registo no servidor é o que importa */
    });
}

/** O erro é mesmo de quota? (o nome/código varia entre browsers) */
export function isQuotaError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const nome = e.name;
  return (
    nome === 'QuotaExceededError' ||
    nome === 'NS_ERROR_DOM_QUOTA_REACHED' || // Firefox
    (e as unknown as { code?: number }).code === 22
  );
}

/** Chaves que guardam histórico de undo/redo — sacrificáveis sob pressão. */
const CHAVES_DE_HISTORICO = /^(jogosHistorico|redoJogosHistorico)/;

function largarHistoricos(): number {
  let removidas = 0;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const chave = localStorage.key(i);
      if (chave && CHAVES_DE_HISTORICO.test(chave)) {
        localStorage.removeItem(chave);
        removidas++;
      }
    }
  } catch {
    /* ignora */
  }
  return removidas;
}

/**
 * Grava um valor, recuperando de falta de espaço em vez de falhar em silêncio.
 * Devolve true se ficou gravado.
 */
export function guardarSeguro(chave: string, valor: string): boolean {
  try {
    localStorage.setItem(chave, valor);
    return true;
  } catch (e) {
    if (!isQuotaError(e)) {
      console.warn(`[storage] Falha a gravar "${chave}":`, e);
      return false;
    }
  }

  // Degrau 2: limpar jogos antigos que já não interessam a ninguém
  console.warn(`[storage] Quota cheia a gravar "${chave}" — a limpar jogos antigos...`);
  cleanupStaleGameState();
  try {
    localStorage.setItem(chave, valor);
    console.warn('[storage] Recuperado depois de limpar jogos antigos.');
    return true;
  } catch (e) {
    if (!isQuotaError(e)) return false;
  }

  // Degrau 3: largar o histórico de undo/redo — o JOGO é que é sagrado
  const largadas = largarHistoricos();
  console.warn(`[storage] Ainda cheia — largado o histórico de undo (${largadas} chave(s)).`);
  try {
    localStorage.setItem(chave, valor);
    console.warn('[storage] Recuperado depois de largar o histórico.');
    reportarFalha(
      'storage_quota_recuperada',
      `Memória do browser encheu a gravar "${chave}"; recuperado após largar o histórico.`,
      { nivel: 'aviso', contexto: { chave, tamanho_kb: Math.round(valor.length / 1024) } }
    );
    return true;
  } catch {
    console.error(
      `[storage] IMPOSSÍVEL gravar "${chave}": espaço esgotado mesmo depois de limpar.`
    );
    // Isto é o pior cenário: o jogo deixou de ser guardado. Antes morria na
    // consola e o utilizador só descobria dias depois.
    reportarFalha(
      'storage_quota_cheia',
      `Não foi possível guardar "${chave}": memória do browser esgotada mesmo depois de limpar.`,
      { nivel: 'critico', contexto: { chave, tamanho_kb: Math.round(valor.length / 1024) } }
    );
    avisarUtilizador('Não consegui guardar o jogo — a memória do browser está cheia.');
    return false;
  }
}

/** Igual ao guardarSeguro mas faz o JSON.stringify (o caso comum). */
export function guardarJSON(chave: string, valor: unknown): boolean {
  try {
    return guardarSeguro(chave, JSON.stringify(valor));
  } catch (e) {
    console.warn(`[storage] Valor não serializável para "${chave}":`, e);
    return false;
  }
}
